import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import { getClients } from "app/services/api/management";
import { getDSOKClientOrders } from "app/services/api/reports";
import {
  Box,
  Breadcrumbs,
  CircularProgress,
  Typography,
  Grid,
  Card,
  TextField,
  MenuItem,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
} from "@mui/material";
import Checkbox from "@mui/material/Checkbox";
import ListItemText from "@mui/material/ListItemText";
import ClientOrderItem from "./ClientOrderItem";
import JumboListNoDataPlaceholder from "@jumbo/components/JumboList/components/JumboListNoDataPlaceHolder";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import PrintIcon from "@mui/icons-material/Print";
import GlobalStyles from "@mui/material/GlobalStyles";

const ClientOrdersList = () => {
  const { t } = useTranslation();
  const { clientId } = useParams();
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [clientName, setClientName] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [isPrinting, setIsPrinting] = useState(false);
  const [filter, setFilter] = useState({
    dateFrom: null,
    dateTo: null,
    month: "today",
    year: new Date().getFullYear(),
    location: null,
    paymentMethods: ["invoice"],
  });

  const dsokSettings = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("dsokSettings"));
    } catch (e) {
      return null;
    }
  }, []);

  const locations = dsokSettings?.customer?.locations || {};
  const locationNames = Object.keys(locations || {});
  const asArray = (val) =>
    Array.isArray(val) ? val : Object.values(val || {});
  const getMethod = (o) =>
    (o?.paymentord?.method || o?.payment?.method || "")
      .toString()
      .toLowerCase();

  const formatDate = (value) => {
    if (!value) return "";
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${dd}.${mm}.${yyyy}`;
  };

  const printRange = useMemo(() => {
    const today = new Date();
    const startOfToday = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate()
    );
    const endOfToday = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate(),
      23,
      59,
      59,
      999
    );

    const firstDay = (year, monthIndex) => new Date(year, monthIndex, 1);
    const lastDay = (year, monthIndex) => new Date(year, monthIndex + 1, 0);

    let from = null;
    let to = null;

    if (filter.month === "custom") {
      from = filter.dateFrom ? new Date(filter.dateFrom) : null;
      to = filter.dateTo ? new Date(filter.dateTo) : null;
    } else if (filter.month === "today") {
      from = startOfToday;
      to = endOfToday;
    } else if (filter.month === "yesterday") {
      const y = new Date(startOfToday);
      y.setDate(y.getDate() - 1);
      const yEnd = new Date(endOfToday);
      yEnd.setDate(yEnd.getDate() - 1);
      from = y;
      to = yEnd;
    } else if (filter.month === "thisMonth") {
      from = firstDay(today.getFullYear(), today.getMonth());
      to = endOfToday;
    } else if (filter.month === "lastMonth") {
      const lmYear =
        today.getMonth() === 0 ? today.getFullYear() - 1 : today.getFullYear();
      const lmMonth = today.getMonth() === 0 ? 11 : today.getMonth() - 1;
      from = firstDay(lmYear, lmMonth);
      to = lastDay(lmYear, lmMonth);
    } else if (filter.month === "thisYear") {
      from = new Date(today.getFullYear(), 0, 1);
      to = endOfToday;
    } else if (filter.month === "lastYear") {
      const ly = today.getFullYear() - 1;
      from = new Date(ly, 0, 1);
      to = new Date(ly, 11, 31, 23, 59, 59, 999);
    } else if (typeof filter.month === "number") {
      const y = Number(filter.year) || today.getFullYear();
      const mIdx = filter.month - 1;
      from = firstDay(y, mIdx);
      to = lastDay(y, mIdx);
    }

    return { from, to };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter.month, filter.dateFrom, filter.dateTo, filter.year]);
  useEffect(() => {
    if (locationNames.length && !filter.location) {
      const firstLocationCode = locations?.[locationNames[0]]?.code;
      setFilter((prev) => ({ ...prev, location: firstLocationCode }));
    }
  }, [locationNames.length]);

  useEffect(() => {
    const fetchClientOrders = async () => {
      setLoading(true);
      const response = await getClients(clientId);
      setClientName(response?.companyName);
      setLoading(false);
    };
    fetchClientOrders();
  }, [clientId]);

  // Detect print mode to render all rows (ignore pagination)
  useEffect(() => {
    const mediaQuery = window.matchMedia("print");
    const handleChange = (e) => setIsPrinting(e.matches);
    if (mediaQuery.addEventListener)
      mediaQuery.addEventListener("change", handleChange);
    else if (mediaQuery.addListener) mediaQuery.addListener(handleChange);

    const handleBeforePrint = () => setIsPrinting(true);
    const handleAfterPrint = () => setIsPrinting(false);
    window.addEventListener("beforeprint", handleBeforePrint);
    window.addEventListener("afterprint", handleAfterPrint);

    return () => {
      if (mediaQuery.removeEventListener)
        mediaQuery.removeEventListener("change", handleChange);
      else if (mediaQuery.removeListener)
        mediaQuery.removeListener(handleChange);
      window.removeEventListener("beforeprint", handleBeforePrint);
      window.removeEventListener("afterprint", handleAfterPrint);
    };
  }, []);

  const updateFilter = (newFilter) => setFilter({ ...filter, ...newFilter });

  const handlePrint = () => {
    try {
      window.print();
    } catch (e) {
      // no-op
    }
  };

  const applyFilter = async () => {
    try {
      setLoading(true);
      const data = await getDSOKClientOrders(
        clientId,
        filter.dateFrom,
        filter.dateTo,
        filter.month,
        filter.year,
        filter.location,
        filter.paymentMethods.join(",")
      );
      const serverOrders = asArray(data?.data?.orders || data || []);
      // Apply all filters locally to ensure consistency with current UI
      const result = serverOrders.filter((order) => {
        const created = order?.createdAt ? new Date(order.createdAt) : null;
        if (!created) return false;

        // Location match (storeCode or locationCode)
        const loc = filter.location;
        if (loc) {
          const storeCode = order?.storeCode || "";
          const locationCode = order?.locationCode || "";
          if (
            !(
              storeCode.includes(loc) ||
              locationCode === loc ||
              locationCode.includes(loc)
            )
          ) {
            return false;
          }
        }

        // Date range by month presets
        const today = new Date();
        const startOfToday = new Date(
          today.getFullYear(),
          today.getMonth(),
          today.getDate()
        );
        const endOfToday = new Date(
          today.getFullYear(),
          today.getMonth(),
          today.getDate(),
          23,
          59,
          59,
          999
        );

        if (filter.month === "today") {
          return created >= startOfToday && created <= endOfToday;
        }
        if (filter.month === "yesterday") {
          const y = new Date(startOfToday);
          y.setDate(y.getDate() - 1);
          const yEnd = new Date(endOfToday);
          yEnd.setDate(yEnd.getDate() - 1);
          return created >= y && created <= yEnd;
        }
        if (filter.month === "thisMonth") {
          return (
            created.getFullYear() === today.getFullYear() &&
            created.getMonth() === today.getMonth()
          );
        }
        if (filter.month === "lastMonth") {
          const lmYear =
            today.getMonth() === 0
              ? today.getFullYear() - 1
              : today.getFullYear();
          const lmMonth = today.getMonth() === 0 ? 11 : today.getMonth() - 1;
          return (
            created.getFullYear() === lmYear && created.getMonth() === lmMonth
          );
        }
        if (filter.month === "thisYear") {
          return created.getFullYear() === today.getFullYear();
        }
        if (filter.month === "lastYear") {
          return created.getFullYear() === today.getFullYear() - 1;
        }
        if (filter.month === "custom") {
          const from = filter.dateFrom ? new Date(filter.dateFrom) : null;
          const to = filter.dateTo ? new Date(filter.dateTo) : null;
          const inFrom = from
            ? created >= new Date(from.setHours(0, 0, 0, 0))
            : true;
          const inTo = to
            ? created <= new Date(to.setHours(23, 59, 59, 999))
            : true;
          return inFrom && inTo;
        }
        // Numeric month (1-12)
        if (typeof filter.month === "number") {
          const monthIdx = filter.month - 1;
          return (
            created.getMonth() === monthIdx &&
            created.getFullYear() === Number(filter.year)
          );
        }
        // Payment method filter
        const method = getMethod(order);
        return filter.paymentMethods.includes(method);
      });
      setOrders(serverOrders);
      setFilteredOrders(result);
    } catch (e) {
      // fall back to client-side filtering
    } finally {
      setLoading(false);
    }
  };

  // Re-filter immediately on payment method change
  useEffect(() => {
    if (!Array.isArray(orders) || orders.length === 0) return;
    const next = orders.filter((o) =>
      filter.paymentMethods.includes(getMethod(o))
    );
    setFilteredOrders(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter.paymentMethods]);

  if (loading) {
    return <CircularProgress />;
  }

  return (
    <Box>
      <GlobalStyles
        styles={{
          "@media print": {
            body: { visibility: "hidden" },
            "#client-orders-print-area, #client-orders-print-area *": {
              visibility: "visible",
            },
            "#client-orders-print-area": {
              position: "absolute",
              left: 0,
              top: 0,
              width: "100%",
            },
            "#client-orders-print-area .MuiTablePagination-root": {
              display: "none",
            },
          },
        }}
      />
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
        <Link to="/">{t("sidebar.menuItem.home")}</Link>
        <Link to="/customer/dsok/clients">
          {t("pages.dsokDetails.clients.title")}
        </Link>
        <Link to={`/customer/dsok/clients/${clientId}`}>{clientName}</Link>
        <Typography color="text.primary">
          {t("pages.clientOrdersList.title")}
        </Typography>
      </Breadcrumbs>
      <Typography variant="h4">
        {clientName} - {t("pages.clientOrdersList.title")}
      </Typography>
      <Grid item md={12} xs={12}>
        <Card>
          <Grid
            container
            columnSpacing={2}
            rowGap={2}
            mb={2}
            className="filters-container"
          >
            <Grid item md={2} xs={12}>
              <Box sx={{ mt: 2, mb: 0, mx: 2 }}>
                <TextField
                  select
                  label={t("pages.dsokReports.selectRange")}
                  value={filter.month || ""}
                  onChange={(e) => updateFilter({ month: e.target.value })}
                  sx={{ width: "100%" }}
                >
                  <MenuItem value="custom">
                    {t("pages.dsokReports.custom")}
                  </MenuItem>
                  <MenuItem value="today">
                    {t("pages.dsokReports.today")}
                  </MenuItem>
                  <MenuItem value="yesterday">
                    {t("pages.dsokReports.yesterday")}
                  </MenuItem>
                  <MenuItem value="thisMonth">
                    {t("pages.dsokReports.thisMonth")}
                  </MenuItem>
                  <MenuItem value="lastMonth">
                    {t("pages.dsokReports.lastMonth")}
                  </MenuItem>
                  <MenuItem value="thisYear">
                    {t("pages.dsokReports.thisYear")}
                  </MenuItem>
                  <MenuItem value="lastYear">
                    {t("pages.dsokReports.lastYear")}
                  </MenuItem>
                  {Array.from({ length: new Date().getMonth() + 1 }, (_, i) => {
                    const monthName = t(
                      `months.${new Date(0, i)
                        .toLocaleString("en", { month: "long" })
                        .toLowerCase()}`
                    );
                    const monthNumber = i + 1;
                    return (
                      <MenuItem key={monthNumber} value={monthNumber}>
                        {monthName + " " + filter.year}
                      </MenuItem>
                    );
                  })}
                </TextField>
              </Box>
            </Grid>
            <Grid item md={4} xs={12}>
              <Box sx={{ mt: 2, mb: 0, mx: 2 }}>
                <TextField
                  select
                  label={t("pages.dsokReports.selectLocation")}
                  value={filter.location || ""}
                  onChange={(e) => updateFilter({ location: e.target.value })}
                  sx={{ width: "100%" }}
                >
                  {locationNames.map((key) => {
                    const locationData = locations[key];
                    const locationCode = locationData?.code;
                    return (
                      <MenuItem value={locationCode} key={locationCode}>
                        {locationData?.name}
                      </MenuItem>
                    );
                  })}
                </TextField>
              </Box>
            </Grid>
            <Grid item md={3} xs={12}>
              <Box sx={{ mt: 2, mb: 0, mx: 2 }}>
                <TextField
                  select
                  SelectProps={{
                    multiple: true,
                    renderValue: (selected) =>
                      (selected || [])
                        .map((pm) => t(`pages.dsokOrders.paymentMethods.${pm}`))
                        .join(", "),
                  }}
                  label={t("pages.dsokOrders.paymentMethodType")}
                  value={filter.paymentMethods}
                  onChange={(e) => {
                    const next = e.target.value;
                    updateFilter({ paymentMethods: next });
                    if (Array.isArray(orders) && orders.length) {
                      const ref = orders.filter((o) =>
                        next.includes(getMethod(o))
                      );
                      setFilteredOrders(ref);
                    }
                  }}
                  sx={{ width: "100%" }}
                >
                  {["card", "invoice"].map((pm) => {
                    const title = t(`pages.dsokOrders.paymentMethods.${pm}`);
                    return (
                      <MenuItem key={pm} value={pm}>
                        <Checkbox
                          checked={filter.paymentMethods.indexOf(pm) > -1}
                        />
                        <ListItemText primary={title} />
                      </MenuItem>
                    );
                  })}
                </TextField>
              </Box>
            </Grid>
            {filter.month && filter.month === "custom" && (
              <>
                <Box sx={{ mt: 2, mb: 0, mx: 2 }}>
                  <TextField
                    label={t("pages.dsokReports.from")}
                    type="date"
                    value={filter.dateFrom || ""}
                    onChange={(e) => updateFilter({ dateFrom: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                </Box>
                <Box sx={{ mt: 2, mb: 0, mx: 2 }}>
                  <TextField
                    label={t("pages.dsokReports.to")}
                    type="date"
                    value={filter.dateTo || ""}
                    onChange={(e) => updateFilter({ dateTo: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                </Box>
              </>
            )}
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                mt: 2,
                mb: 0,
                mx: 2,
              }}
            >
              <Button variant="contained" color="primary" onClick={applyFilter}>
                {t("pages.dsokReports.submit")}
              </Button>
              <Tooltip title="Print">
                <IconButton
                  color="primary"
                  onClick={handlePrint}
                  sx={{ ml: 1 }}
                >
                  <PrintIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Grid>
        </Card>
      </Grid>

      {loading ? (
        <CircularProgress />
      ) : !Array.isArray(filteredOrders) || filteredOrders.length === 0 ? (
        <JumboListNoDataPlaceholder />
      ) : (
        <Card id="client-orders-print-area" sx={{ mt: 2 }}>
          <Grid item xs={12}>
            <Box sx={{ px: 2, pb: 2 }}>
              <Box
                sx={{
                  display: "none",
                  mb: 2,
                  "@media print": { display: "block" },
                }}
              >
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: "left", padding: "4px 0" }}>
                        {t("pages.dsokOrders.customer")}
                      </th>
                      <th style={{ textAlign: "left", padding: "4px 0" }}>
                        {t("pages.dsokReports.from")}
                      </th>
                      <th style={{ textAlign: "left", padding: "4px 0" }}>
                        {t("pages.dsokReports.to")}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ padding: "4px 0" }}>{clientName}</td>
                      <td style={{ padding: "4px 0" }}>
                        {formatDate(printRange.from)}
                      </td>
                      <td style={{ padding: "4px 0" }}>
                        {formatDate(printRange.to)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </Box>
              <TablePagination
                lang={t}
                rowsPerPageOptions={[5, 20, 30, 40, 50, 100]}
                component="div"
                count={filteredOrders.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
              />
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t("pages.dsokReports.orderId")}</TableCell>
                    <TableCell>
                      {t("pages.dsokOrders.paymentMethodType")}
                    </TableCell>
                    <TableCell>{t("pages.presentation.createdAt")}</TableCell>
                    <TableCell align="right">
                      {t("pages.dsokReports.grandTotalWithoutVat")}
                    </TableCell>
                    <TableCell align="right">
                      {t("pages.dsokReports.vatTotal")}
                    </TableCell>
                    <TableCell align="right">
                      {t("pages.dsokReports.grandTotal")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(isPrinting
                    ? filteredOrders
                    : filteredOrders.slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage
                      )
                  ).map((order, idx) => (
                    <ClientOrderItem
                      key={order._id}
                      order={order}
                      currencyCode={dsokSettings?.customer?.currencyCode}
                      isFirst={page === 0 && idx === 0}
                    />
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                rowsPerPageOptions={[10, 20, 40, 50, 100]}
                component="div"
                count={filteredOrders.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
              />
            </Box>
          </Grid>
        </Card>
      )}
    </Box>
  );
};

export default ClientOrdersList;
